'use client';

/**
 * Feature Gate Component
 * Wraps page content and hides client-rendered UI when a feature flag is disabled.
 *
 * This is not an access-control boundary. API routes and server-rendered data
 * still need their own authorization/feature checks.
 *
 * Usage:
 * ```tsx
 * <FeatureGate featureTier="prototype">
 *   <YourPageContent />
 * </FeatureGate>
 * ```
 */

import { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { Lock, Beaker, FlaskConical, Sparkles, ArrowLeft, User } from 'lucide-react';
import { useFeatureFlags } from '@/components/shell/AppShellContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  getFeatureTierForRoute,
  isFeatureAccessible,
  FEATURE_TIERS,
  type FeatureTier,
} from '@/lib/features';

/**
 * Icon mapping for feature tiers
 */
const TIER_ICONS: Record<FeatureTier, typeof Beaker> = {
  prototype: Beaker,
  beta: FlaskConical,
  alpha: Sparkles,
};

interface FeatureGateProps {
  /** Children to render when feature is enabled */
  children: ReactNode;
  /** Explicitly specify the feature tier (optional - auto-detected from route if not provided) */
  featureTier?: FeatureTier;
  /** Custom title for the gate message */
  title?: string;
  /** Custom description for the gate message */
  description?: string;
}

/**
 * Component shown when a feature is not enabled
 */
function FeatureNotEnabledMessage({
  tier,
  title,
  description,
}: {
  tier: FeatureTier;
  title?: string;
  description?: string;
}) {
  const config = FEATURE_TIERS[tier];
  const Icon = TIER_ICONS[tier];

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8">
      <Card className="max-w-lg bg-slate-900/80 border-slate-700">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 p-4 rounded-full bg-slate-800/50 border border-slate-700">
            <Lock className="h-8 w-8 text-slate-400" />
          </div>
          <CardTitle className="text-xl text-white flex items-center justify-center gap-2">
            {title || 'Feature Not Enabled'}
          </CardTitle>
          <CardDescription className="text-slate-400 mt-2">
            {description || `This page is part of ${config.name} and is currently disabled.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Feature Tier Info */}
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${config.badgeColor.split(' ')[0]}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-white">{config.name}</span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] uppercase tracking-wider ${config.badgeColor}`}
                  >
                    {tier}
                  </Badge>
                </div>
                <p className="text-sm text-slate-400">
                  {config.description}
                </p>
              </div>
            </div>
          </div>

          {/* Enable Instructions */}
          <div className="text-center text-sm text-slate-500">
            <p>To access this feature:</p>
            <p className="mt-1">
              Go to <span className="text-slate-300">Profile Settings</span> → <span className="text-slate-300">Preview Features</span>
            </p>
            <p className="mt-1">
              Enable <span className="text-violet-400">{config.name}</span>
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1 border-slate-700 hover:bg-slate-800"
              asChild
            >
              <Link href="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Home
              </Link>
            </Button>
            <Button
              className="flex-1 bg-violet-600 hover:bg-violet-700"
              asChild
            >
              <Link href="/profile">
                <User className="h-4 w-4 mr-2" />
                Profile Settings
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Feature Gate component that wraps page content
 */
export function FeatureGate({
  children,
  featureTier,
  title,
  description,
}: FeatureGateProps) {
  const pathname = usePathname();
  const featureFlags = useFeatureFlags();

  // Determine the feature tier (explicit prop or auto-detect from route)
  const tier = featureTier || getFeatureTierForRoute(pathname);

  // If no tier is associated with this route, render children normally
  if (!tier) {
    return <>{children}</>;
  }

  // Check if the feature is enabled.
  //
  // `=== true` was always false. Feature flags are the TRI-STATE strings
  // 'disabled' | 'warning' | 'enabled' (see lib/features/config.ts), set by the
  // Profile -> Preview Features toggle, and a string is never boolean true. So
  // this gate was permanently closed: a user could switch "Alpha Features" on —
  // which the gate card itself instructs them to do — and nothing would change,
  // sending them to look for a persistence or hydration bug that does not exist.
  //
  // isFeatureAccessible() is the correct reader and already handles the legacy
  // boolean shape too. RightPanel.tsx has always used it via
  // isInCallFeatureEnabled(); only this gate read the raw value.
  const config = FEATURE_TIERS[tier];
  const isEnabled = isFeatureAccessible(featureFlags[config.flagKey]);

  // If feature is enabled, render children
  if (isEnabled) {
    return <>{children}</>;
  }

  // Feature is not enabled - show gate message
  return (
    <FeatureNotEnabledMessage
      tier={tier}
      title={title}
      description={description}
    />
  );
}

/**
 * Hook to check if current route's feature is enabled
 * Useful for conditional rendering within pages
 */
export function useRouteFeatureEnabled(): boolean {
  const pathname = usePathname();
  const featureFlags = useFeatureFlags();

  const tier = getFeatureTierForRoute(pathname);
  if (!tier) return true; // Not a gated route

  const config = FEATURE_TIERS[tier];
  // Same tri-state bug as FeatureGate above — see the comment there.
  return isFeatureAccessible(featureFlags[config.flagKey]);
}

export default FeatureGate;
